import { useState } from "react";
import { Wrench, ShoppingBag, Home, Briefcase, Sparkles, Megaphone } from "lucide-react";
import PhoneMockup from "@/components/whatsapp/PhoneMockup";
import { SectionCta } from "@/components/whatsapp/WhatsappSections";
import { trackEvent } from "@/lib/tracking";

const INDUSTRIES = [
  { id: "services", label: "Service businesses", Icon: Wrench,
    desc: "Capture enquiries, answer service questions and qualify quotation requests.",
    msgs: [{ from: "user", text: "Hi, do you service my area?" }, { from: "bot", text: "Happy to help. Which area are you in, and what do you need done?", options: ["Repairs", "Installation", "Get a quote"] }] },
  { id: "retail", label: "Retail & eCommerce", Icon: ShoppingBag,
    desc: "Answer product questions, provide available order information and assist customers with support enquiries.",
    msgs: [{ from: "user", text: "Is this available in size M?" }, { from: "bot", text: "Let me help with that. What do you need?", options: ["Product availability", "Delivery info", "Speak to support"] }] },
  { id: "property", label: "Property businesses", Icon: Home,
    desc: "Collect property preferences, respond to enquiries and route prospects to agents.",
    msgs: [{ from: "user", text: "I'm looking for a 2-bedroom to rent." }, { from: "bot", text: "Great. Which area and what monthly budget do you have in mind?" }, { from: "system", text: "Routed to rentals agent" }] },
  { id: "professional", label: "Professional services", Icon: Briefcase,
    desc: "Capture consultation requests, answer routine questions and direct enquiries to the appropriate team.",
    msgs: [{ from: "user", text: "I'd like to book a consultation." }, { from: "bot", text: "Certainly. What is the consultation about?", options: ["Tax", "Accounting", "Something else"] }] },
  { id: "beauty", label: "Beauty & wellness", Icon: Sparkles,
    desc: "Manage appointment enquiries, service questions and booking requests.",
    msgs: [{ from: "user", text: "Can I book a facial on Saturday?" }, { from: "bot", text: "Let's find a time. Which part of the day suits you?", options: ["Morning", "Afternoon"] }] },
  { id: "ads", label: "Businesses running paid ads", Icon: Megaphone,
    desc: "Respond to incoming enquiries from advertising campaigns and capture customer information for follow-up.",
    msgs: [{ from: "user", text: "I saw your ad — what does it cost?" }, { from: "bot", text: "Thanks for reaching out. Pricing depends on your needs — may I ask a few quick questions?", options: ["Yes, go ahead", "Talk to a person"] }] },
];

export default function WhatsappIndustries() {
  const [active, setActive] = useState(INDUSTRIES[0].id);
  const industry = INDUSTRIES.find((i) => i.id === active);

  return (
    <section data-testid="wa-industries-section" className="relative py-24 sm:py-32 bg-deep-blue overflow-hidden text-white">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/4 right-0 h-[500px] w-[500px] rounded-full bg-cyan-400/20 blur-[120px]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.2fr_auto] gap-12 lg:gap-16 items-start">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-white/50 font-mono mb-4">Business use cases</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05]">
            WhatsApp Automation for Different Types of Businesses
          </h2>
          <p className="mt-5 text-white/70 max-w-xl">Choose a business type to see how WhatsApp customer support and lead capture can work.</p>

          <div className="mt-8 grid sm:grid-cols-2 gap-3" role="tablist">
            {INDUSTRIES.map((i) => {
              const on = i.id === active;
              return (
                <button key={i.id} type="button" role="tab" aria-selected={on} data-testid={`wa-industry-${i.id}`}
                  onClick={() => { setActive(i.id); trackEvent("industry_selected", { industry: i.id, service: "whatsapp" }); }}
                  className={`text-left rounded-2xl border p-4 transition-[background-color,border-color] ${on ? "bg-white text-zinc-900 border-white" : "bg-white/[0.04] border-white/15 hover:bg-white/10"}`}>
                  <span className="flex items-center gap-2 font-display font-bold">
                    <i.Icon className={`h-4 w-4 ${on ? "text-[#128c4b]" : "text-[#25d366]"}`} /> {i.label}
                  </span>
                  <span className={`mt-1.5 block text-sm leading-relaxed ${on ? "text-zinc-600" : "text-white/65"}`}>{i.desc}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-8">
            <SectionCta label="Get a WhatsApp Automation Quote" location="wa_industries" testid="wa-industries-cta" dark />
          </div>
        </div>

        <div className="mx-auto lg:mt-16">
          <div className="text-center mb-3">
            <span className="inline-block rounded-full bg-white/10 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.16em] text-white/80">
              Example · simulated conversation
            </span>
          </div>
          <PhoneMockup key={industry.id} title={industry.label} businessName="Your Business" messages={industry.msgs} size="md" />
        </div>
      </div>
    </section>
  );
}
